import type { Applicant, ApplicantPatch } from "@/models/application";
import * as jsonPatch from "fast-json-patch";
import { sanitizeNationalIdNumber, scrubNationalIdNumberPatch } from "@/utils/nationalIdNumber";
import { NationalIdNumberTypeIds } from "@/config/constants";

export default function useApplicantPatch(store: any, updatedApplicant: Applicant) {
  interface Patch {
    op: string;
    path: string;
    value?: unknown;
  }

  /// filter out paths
  const ignoredPaths = [
    "addresses",
    "incomes",
    "occupation",
    "employments",
    "surveyResponses",
    "softPullRatePlans",
    "id",
    "applicantTypeName",
    "submittedAt",
    "citizenshipStatusName",
    "contactInfo/phoneNumberTypeName",
    "contactInfo/preferredContactTypeName",
    "membership/order",
    "membership/statusChangedAtUtc",
    "membership/isJointOnStudentAccount",
    "membership/membershipStatusName",
  ];

  function mapApplicantToCompareObj(applicant: any) {
    // ignores [DoNotPatch] and [IgnorePatch] attributes
    return {
      userId: applicant?.userId,
      applicantTypeId: applicant?.applicantTypeId,
      firstName: applicant?.firstName,
      middleName: applicant?.middleName,
      lastName: applicant?.lastName,
      dateOfBirth: applicant?.dateOfBirth,
      citizenshipStatusId: applicant?.citizenshipStatusId,
      userAgent: applicant?.userAgent,
      ipAddress: applicant?.ipAddress,
      nationalIdNumber: applicant?.nationalIdNumber,
      nationalIdNumberTypeId: applicant?.nationalIdNumberTypeId,
      contactInfo:
        applicant?.contactInfo !== null && applicant?.contactInfo !== undefined
          ? {
              phoneNumber: applicant?.phoneNumber,
              phoneNumberTypeId: applicant?.phoneNumberTypeId,
              emailAddress: applicant?.emailAddress,
              preferredContactTypeId: applicant?.preferredContactTypeId,
            }
          : null,
      membership:
        applicant?.membership !== null && applicant?.membership !== undefined
          ? {
              isMember: applicant?.membership?.isMember,
              memberId: applicant?.membership?.memberId,
              isJointOnStudentAccount: applicant?.membership?.isJointOnStudentAccount,
            }
          : null,
      concierge:
        applicant?.concierge !== null && applicant?.concierge !== undefined
          ? {
              conciergeConsent: applicant?.concierge?.conciergeConsent,
              preferredContactTypeId: applicant?.concierge?.preferredContactTypeId,
            }
          : null,
      spouse:
        applicant?.spouse !== null && applicant?.spouse !== undefined
          ? {
              firstName: applicant.spouse?.firstName,
              middleName: applicant?.spouse?.middleName,
              lastName: applicant?.spouse?.lastName,
              emailAddress: applicant?.spouse?.emailAddress,
              sameAddress: applicant?.spouse?.sameAddress,
              spouseAddress: {
                addressTypeId: applicant?.spouse?.spouseAddress?.addressTypeId,
                addressUnitTypeId: applicant?.spouse?.spouseAddress?.addressUnitTypeId,
                address1: applicant?.spouse?.spouseAddress?.address1,
                address2: applicant?.spouse?.spouseAddress?.address2,
                city: applicant?.spouse?.spouseAddress?.city,
                stateId: applicant?.spouse?.spouseAddress?.stateId,
                countryId: applicant?.spouse?.spouseAddress?.countryId,
                postalCode: applicant?.spouse?.spouseAddress?.postalCode,
              },
            }
          : null,
    };
  }

  function isValidPath(patch: Patch) {
    return !ignoredPaths.some(ignoredPath =>
      (patch?.path || "").toLowerCase()?.startsWith(`/${ignoredPath?.toLowerCase()}`),
    );
  }

  function handlePatches(patches: jsonPatch.Operation[]) {
    // if updating nationalIdNumber, make sure nationalIdNumberTypeId patch exists
    // HandleNationalIdNumberOperation requires the nationalIdNumberTypeId value to exist otherwise it overwrites it with the SSN type
    if (
      patches.find((p: any) => p.path.includes("/nationalIdNumber")) &&
      !patches.find((p: any) => p.path.includes("/nationalIdNumberTypeId"))
    ) {
      patches.push({
        op: "replace",
        path: "/nationalIdNumberTypeId",
        value: updatedApplicant.nationalIdNumberTypeId ?? NationalIdNumberTypeIds.SSN,
      });
    }

    const filteredApplicantPatch = scrubNationalIdNumberPatch(patches);

    return filteredApplicantPatch;
  }

  function gatherApplicantPatches(): Array<ApplicantPatch> {
    const applicantPatches: Array<ApplicantPatch> = [];

    if (updatedApplicant && updatedApplicant?.id > 0) {
      const currentApplicant =
        store.state.appData.applicants.find((a: Applicant) => a.id === updatedApplicant?.id) ?? ({} as Applicant);

      console.log("APPLICANT PATCH FUNCTIONALITY", {
        currentApplicant,
        updatedApplicant: updatedApplicant,
        compareObjA: mapApplicantToCompareObj(currentApplicant),
        compareObjB: mapApplicantToCompareObj(updatedApplicant),
      });

      // Only include the SSN when it is a genuine 9-digit value the applicant
      // typed. A masked ("*****1234"), empty, or corrupted value is omitted so
      // no /nationalIdNumber operation is generated (preventing a bad value
      // from overwriting or blanking the saved SSN).
      const sanitizedNationalIdNumber = sanitizeNationalIdNumber(updatedApplicant?.nationalIdNumber);

      if (sanitizedNationalIdNumber) {
        updatedApplicant.nationalIdNumber = sanitizedNationalIdNumber;
      }

      // Diff the original store applicant against the updated copy. The form
      // builds `updatedApplicant` as a separate deep clone and never mutates the
      // observed store object, so an observer-based `jsonPatch.generate` would
      // return an empty patch (dropping firstName/lastName/SSN/DOB/citizenship).
      // `currentApplicant` still references the original (pre-update) object, so
      // comparing it to `updatedApplicant` yields the correct patch.
      const patches = jsonPatch.compare(currentApplicant, updatedApplicant);

      if (patches.length > 0) {
        const cleanPatches = handlePatches(patches);

        applicantPatches.push({
          applicantId: currentApplicant.id,
          patch: cleanPatches,
        });
      }

      // Keep the store's display in sync with the submitted values until the
      // server round-trip returns and refreshes appData.
      store.dispatch("setApplicant", updatedApplicant);

      console.log("Patches before filtering", { applicantPatches });

      if (applicantPatches.length > 0) {
        applicantPatches?.forEach(applicantPatch => {
          if ((applicantPatch.patch || [])?.length > 0) {
            applicantPatch.patch = applicantPatch.patch?.filter((p: Patch) => isValidPath(p));
          }
        });

        const filteredApplicantPatches = applicantPatches.filter(
          p => p.applicantId !== undefined && p.patch?.length > 0,
        );

        console.log("Patches after filtering", { filteredApplicantPatches });

        return filteredApplicantPatches;
      }
    }

    return applicantPatches;
  }
  return { gatherApplicantPatches, mapApplicantToCompareObj };
}
